import axios from 'axios';
import { API_BASE_URI } from 'library/constants/api';

function getAuthHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  };
}

export interface ZohoPaymentsSession {
  payments_session_id: string;
  amount: string;
  currency: string;
  description: string;
  invoice_number: string;
  reference_number: string;
}

export interface HostPlanPurchaseSession {
  purchaseId: number;
  bhkType: number;
  billingCycle: string;
  priceInr: number;
  zoho: {
    code: number;
    message: string;
    payments_session: ZohoPaymentsSession;
  };
}

export interface HostPlanPurchaseSummary {
  id: number;
  bhkType: number;
  billingCycle: string;
  priceInr: number;
  status: 'PENDING' | 'SUCCESS' | 'FAILED';
  propertyId: number | null;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
}

/** One of the host's active listings with the plan it is on today (GET /payments/hostplan/listings). */
export interface HostListingPlan {
  propertyId: number;
  title: string | null;
  city: string | null;
  /** The listing's bedroom count, which a Host Pro plan has to match; null when it has none. */
  bhkType: number | null;
  status: 'HOST_PRO' | 'STARTER';
  purchaseId: number | null;
  billingCycle: string | null;
  startDate: string | null;
  endDate: string | null;
}

class HostPlanPurchaseService {
  static async createSession(bhkType: number, billingCycle: string): Promise<HostPlanPurchaseSession> {
    const res = await axios.post(
      `${API_BASE_URI}/payments/hostplan/session`,
      { bhkType, billingCycle },
      { headers: getAuthHeaders() },
    );
    return res.data;
  }

  static async verify(purchaseId: number): Promise<{ status: 'PENDING' | 'SUCCESS' | 'FAILED' }> {
    const res = await axios.post(
      `${API_BASE_URI}/payments/hostplan/verify`,
      { purchaseId },
      { headers: getAuthHeaders() },
    );
    return res.data;
  }

  /** All of the caller's purchases, tagged and untagged — used to resurface an unclaimed plan. */
  static async listMine(): Promise<HostPlanPurchaseSummary[]> {
    const res = await axios.get(`${API_BASE_URI}/payments/hostplan/mine`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  }

  /** The caller's active listings with their plan: Host Pro ones first, then Starter. */
  static async listListings(): Promise<HostListingPlan[]> {
    const res = await axios.get(`${API_BASE_URI}/payments/hostplan/listings`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  }

  /** Ties a SUCCESS, untagged purchase to one BHK-matching listing. Permanent once set. */
  static async tagProperty(purchaseId: number, propertyId: number): Promise<void> {
    await axios.post(
      `${API_BASE_URI}/payments/hostplan/${purchaseId}/tag`,
      { propertyId },
      { headers: getAuthHeaders() },
    );
  }

  /** The host picked the Starter plan: guests then see generic, brand-free title/description. */
  static async chooseStarter(propertyId: number | string): Promise<void> {
    await axios.post(`${API_BASE_URI}/hostPro/${propertyId}/choose-starter`, null, {
      headers: getAuthHeaders(),
    });
  }

  /** True while the listing is on generic Starter copy (so its title/description are locked). */
  static async isStarterCopy(propertyId: number | string): Promise<boolean> {
    const res = await axios.get(`${API_BASE_URI}/hostPro/${propertyId}/starter-copy`, {
      headers: getAuthHeaders(),
    });
    return !!res.data?.starter;
  }

  /** The Starter copy guests will see: the host's title and description with the brand name removed. */
  static async starterPreview(
    propertyId: number | string,
  ): Promise<{ title: string; description: string }> {
    const res = await axios.get(`${API_BASE_URI}/hostPro/${propertyId}/starter-preview`, {
      headers: getAuthHeaders(),
    });
    return { title: String(res.data?.title || ''), description: String(res.data?.description || '') };
  }
}

export default HostPlanPurchaseService;
